// src/components/BrandNotificationBell.tsx
//
// Platform notification bell dropdown (§11.4).
//
// Backed by real persisted platform notifications (EXPIRY_ALERT, LOW_BALANCE, TOPUP_SUCCESS,
// BLOG_GENERATED, BLOG_GENERATION_FAILED, SMM_CONTENT_GENERATED, etc.) via usePlatformNotifications.
// Includes a dedicated, on-demand 5s poller that activates only during active background blog generation
// and immediately stops once the completion notification is received.

import { useState, useRef, useEffect, useCallback } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  usePlatformNotifications,
  NOTIFICATIONS_KEY,
  UNREAD_COUNT_KEY,
} from '../hooks/usePlatformNotifications'
import {
  fetchPlatformNotifications,
  fetchUnreadCount,
  type PlatformNotification,
  type PlatformNotificationType,
} from '../services/platformNotificationsApi'
import { useCredits } from '../hooks/useCredits'
import { useTelemetry } from '../hooks/useTelemetry'
import { MODULES } from '../telemetry/constants/modules'
import { ACTIONS } from '../telemetry/constants/actions'

const goTo = (path: string) => {
  if (window.REACT_APP_NAVIGATE) {
    window.REACT_APP_NAVIGATE(path)
  } else {
    window.location.href = path
  }
}

export default function BrandNotificationBell() {
  const { trackAction } = useTelemetry({ module: MODULES.SUPPORT, submodule: 'Notification Bell' })
  const [isOpen, setIsOpen] = useState(false)
  const [hasNewAlert, setHasNewAlert] = useState(false)
  const [isGenerating, setIsGenerating] = useState(() => {
    return localStorage.getItem('blog_generation_active') === 'true'
  })
  const dropdownRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    isLoading,
    notificationsQuery,
  } = usePlatformNotifications()

  const { balance } = useCredits()
  const hasActiveCreditWarning = Boolean(
    balance?.is_low_credit || balance?.is_expiring_soon || balance?.is_expired
  )

  // Track known notification IDs so we only alert for genuinely new completions
  const seenNotificationIdsRef = useRef<Set<number>>(new Set())
  const isInitialLoadDoneRef = useRef(false)

  // Initialize seen IDs from initial query data
  useEffect(() => {
    if (notifications.length > 0 && !isInitialLoadDoneRef.current) {
      notifications.forEach((n) => seenNotificationIdsRef.current.add(n.id))
      isInitialLoadDoneRef.current = true
    }
  }, [notifications])

  // Listen for global background generation lifecycle events
  useEffect(() => {
    const handleGenStarted = () => {
      setIsGenerating(true)
    }
    const handleGenCompleted = () => {
      setIsGenerating(false)
    }
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'blog_generation_active') {
        setIsGenerating(e.newValue === 'true')
      }
    }

    window.addEventListener('platform_generation_started', handleGenStarted)
    window.addEventListener('platform_generation_completed', handleGenCompleted)
    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener('platform_generation_started', handleGenStarted)
      window.removeEventListener('platform_generation_completed', handleGenCompleted)
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  const handleNotificationClick = useCallback(
    (notification: PlatformNotification) => {
      if (!notification.is_read) {
        markAsRead(notification.id)
        trackAction(ACTIONS.MARK_READ, 'Mark Notification as Read', { notification_id: notification.id })
      }
      setIsOpen(false)
      setHasNewAlert(false)

      if (notification.type === 'BLOG_GENERATED') {
        localStorage.setItem('blog_active_tab', 'generated')
        if (window.REACT_APP_NAVIGATE) {
          window.REACT_APP_NAVIGATE('/dashboard#blog', {
            state: { section: 'blog', subtab: 'generated', blogId: notification.reference_id },
          })
        } else {
          window.location.href = '/dashboard#blog'
        }
        window.dispatchEvent(
          new CustomEvent('switch_dashboard_section', {
            detail: { section: 'blog', subtab: 'generated', blogId: notification.reference_id },
          })
        )
        window.dispatchEvent(
          new CustomEvent('navigate_to_tab', {
            detail: { tab: 'blog', subtab: 'generated', blogId: notification.reference_id },
          })
        )
      } else if (notification.type === 'BLOG_GENERATION_FAILED') {
        localStorage.setItem('blog_active_tab', 'discovery')
        if (window.REACT_APP_NAVIGATE) {
          window.REACT_APP_NAVIGATE('/dashboard#blog', {
            state: { section: 'blog', subtab: 'discovery' },
          })
        } else {
          window.location.href = '/dashboard#blog'
        }
        window.dispatchEvent(
          new CustomEvent('switch_dashboard_section', {
            detail: { section: 'blog', subtab: 'discovery' },
          })
        )
        window.dispatchEvent(
          new CustomEvent('navigate_to_tab', {
            detail: { tab: 'blog', subtab: 'discovery' },
          })
        )
      } else if (
        notification.type === 'SMM_CONTENT_GENERATED' ||
        notification.type === 'SMM_CONTENT_GENERATION_FAILED'
      ) {
        // reference_id is the generated post — the workspace opens its editor.
        const params = new URLSearchParams({ section: 'content-calendar-workspace' })
        if (notification.reference_id) params.set('post', notification.reference_id)
        goTo(`/social-media-manager?${params.toString()}`)
      } else if (notification.type === 'LOW_BALANCE' || notification.type === 'TOPUP_SUCCESS') {
        goTo('/credits')
      } else if (notification.type === 'EXPIRY_ALERT') {
        // Same destination as the expiry popup's Renew button.
        goTo('/profile?tab=subscription')
      }
    },
    [markAsRead, trackAction]
  )

  // ── Dedicated On-Demand 5s Poller for Background Generation ────────────────
  useEffect(() => {
    if (!isGenerating) return

    let pollCount = 0
    const maxPollCount = 60 // 5 minutes max safety timeout (60 * 5s)

    const pollInterval = setInterval(async () => {
      pollCount++
      try {
        const [freshNotifs, freshUnread] = await Promise.all([
          fetchPlatformNotifications(10, 0),
          fetchUnreadCount(),
        ])

        queryClient.setQueryData(NOTIFICATIONS_KEY, freshNotifs)
        queryClient.setQueryData(UNREAD_COUNT_KEY, freshUnread)

        // Check for new notifications
        const newlyArrived = freshNotifs.filter(
          (n) => !seenNotificationIdsRef.current.has(n.id) && !n.is_read
        )

        for (const notif of newlyArrived) {
          seenNotificationIdsRef.current.add(notif.id)

          if (notif.type === 'BLOG_GENERATED') {
            localStorage.removeItem('blog_generation_active')
            setIsGenerating(false)
            setHasNewAlert(true)
            toast.success(notif.title || 'Blog Post Ready', {
              description: notif.message || 'Your blog post has been successfully generated.',
              duration: 8000,
              action: {
                label: 'View Blog',
                onClick: () => handleNotificationClick(notif),
              },
            })
            window.dispatchEvent(new CustomEvent('platform_generation_completed'))
            break
          } else if (notif.type === 'BLOG_GENERATION_FAILED') {
            localStorage.removeItem('blog_generation_active')
            setIsGenerating(false)
            setHasNewAlert(true)
            toast.error(notif.title || 'Blog Generation Failed', {
              description: notif.message || 'Blog generation could not be completed.',
              duration: 8000,
            })
            window.dispatchEvent(new CustomEvent('platform_generation_completed'))
            break
          } else {
            setHasNewAlert(true)
          }
        }
      } catch (pollErr) {
        console.warn('[BrandNotificationBell] On-demand poll failed:', pollErr)
      }

      if (pollCount >= maxPollCount) {
        localStorage.removeItem('blog_generation_active')
        setIsGenerating(false)
      }
    }, 5000)

    return () => clearInterval(pollInterval)
  }, [isGenerating, queryClient, handleNotificationClick])

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Track dropdown open/view telemetry and ensure notifications are fresh
  useEffect(() => {
    if (isOpen) {
      notificationsQuery.refetch()
      trackAction(ACTIONS.MARK_READ, 'Open Notifications Panel')
    }
  }, [isOpen, trackAction, notificationsQuery])

  const getIconForType = (type: PlatformNotificationType) => {
    switch (type) {
      case 'EXPIRY_ALERT':
        return <i className="ri-calendar-warning-line text-amber-500 bg-amber-50 p-1.5 rounded-lg text-lg" />
      case 'LOW_BALANCE':
        return <i className="ri-error-warning-line text-rose-500 bg-rose-50 p-1.5 rounded-lg text-lg" />
      case 'TOPUP_SUCCESS':
        return <i className="ri-checkbox-circle-line text-emerald-500 bg-emerald-50 p-1.5 rounded-lg text-lg" />
      case 'BLOG_GENERATED':
        return <i className="ri-article-line text-blue-600 bg-blue-50 p-1.5 rounded-lg text-lg" />
      case 'BLOG_GENERATION_FAILED':
        return <i className="ri-error-warning-line text-rose-500 bg-rose-50 p-1.5 rounded-lg text-lg" />
      case 'SMM_CONTENT_GENERATED':
        return <i className="ri-image-2-line text-blue-600 bg-blue-50 p-1.5 rounded-lg text-lg" />
      case 'SMM_CONTENT_GENERATION_FAILED':
        return <i className="ri-error-warning-line text-rose-500 bg-rose-50 p-1.5 rounded-lg text-lg" />
      default:
        return <i className="ri-notification-3-line text-slate-500 bg-slate-50 p-1.5 rounded-lg text-lg" />
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        id="notification-bell-button"
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
          setHasNewAlert(false)
        }}
        className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer relative ${
          hasNewAlert
            ? 'text-rose-600 bg-rose-50 ring-2 ring-rose-400 shadow-md shadow-rose-200/50'
            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
        }`}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}${
          hasActiveCreditWarning ? ' — credits or subscription needs attention' : ''
        }`}
      >
        <i className={`ri-notification-3-line text-lg ${hasNewAlert ? 'animate-bounce' : ''}`} />

        {/* Outer animated ripple ring when a new alert is triggered */}
        {hasNewAlert && (
          <span className="absolute -inset-1 rounded-lg bg-rose-400/30 animate-ping pointer-events-none" />
        )}

        {/* Unread count badge */}
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white shadow-sm ring-1 ring-rose-400/50 animate-pulse"
            role="status"
            aria-label={`${unreadCount} unread notifications`}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 sm:w-96 bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200 z-50 overflow-hidden flex flex-col max-h-[80vh]">
          {/* Header */}
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-slate-800">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-xs font-semibold bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => {
                  markAllAsRead()
                  trackAction(ACTIONS.MARK_ALL_READ, 'Mark All Notifications as Read')
                }}
                className="text-xs font-semibold text-teal-600 hover:text-teal-700 cursor-pointer"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center text-slate-400">
                <i className="ri-loader-4-line animate-spin text-2xl" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 flex flex-col items-center justify-center text-center">
                <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
                  <i className="ri-notification-off-line text-2xl text-slate-300" />
                </div>
                <p className="text-sm font-semibold text-slate-500">No notifications yet</p>
                <p className="text-xs text-slate-400 mt-1">You're all caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.slice(0, 10).map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 flex gap-3 hover:bg-slate-50 transition-colors cursor-pointer ${
                      !notification.is_read ? 'bg-blue-50/30' : ''
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {getIconForType(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0 pr-4 relative">
                      <div className="flex justify-between items-start mb-1">
                        <p className={`text-sm truncate pr-2 ${!notification.is_read ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'}`}>
                          {notification.title}
                        </p>
                        <span className="text-[10px] font-medium text-slate-400 whitespace-nowrap mt-0.5 flex-shrink-0">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className={`text-xs line-clamp-2 ${!notification.is_read ? 'text-slate-600 font-medium' : 'text-slate-500'}`}>
                        {notification.message}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <div className="flex-shrink-0 mt-1.5">
                        <div className="w-2 h-2 rounded-full bg-teal-500 shadow-sm shadow-teal-500/20" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 10 && (
            <div className="p-3 border-t border-slate-100 bg-slate-50 text-center">
              <span className="text-xs font-medium text-slate-500">
                Showing latest 10 notifications
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

